import React from 'react';
import { Icon, Modal } from 'semantic-ui-react';
import styled from '@emotion/styled';

const defaultErrorMessage =
  'The email has failed to send through HudPro. It has been downloaded and MUST be sent in Outlook!';
const defaultAttachmentSizeErrorMessage =
  'The email could not be sent because the attachments may be too large.';
const attachmentSizeChoiceHint =
  'You can either download the email to send manually, or compress the PDFs and try again.';
const getCompressionUnavailableHint = (multipleAttachments: boolean) =>
  `Compressing the ${multipleAttachments ? 'files' : 'file'} did not make ${
    multipleAttachments ? 'them' : 'it'
  } small enough for the email to send successfully.${
    multipleAttachments ? ' If you are sending multiple attachments, try sending them separately.' : ''
  } You can also download the email and try sending it manually.`;
const defaultModalHeight = 285;
const attachmentSizeFailureModalHeight = 330;
const compressionUnavailableModalHeight = 390;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: transparent;
  border: 0;
  color: #444;
  cursor: pointer;
  padding: 15px;
`;

const ModalBody = styled.div`
  padding: 0 35px;
`;

const ModalHeader = styled.div`
  color: #4d4e4d;
  font-family: Lato;
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
`;

const ModalInfo = styled.div`
  color: #4d4e4d;
  font-family: Lato;
  font-size: 16px;
  line-height: 24px;
  margin-top: 24px;
  min-height: 48px;
  white-space: pre-wrap;
  width: 320px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 9px;
  justify-content: flex-end;
  margin-top: 45px;
`;

const BaseButton = styled.button`
  border-radius: 5px;
  box-sizing: border-box;
  cursor: pointer;
  font-family: Lato;
  font-size: 14px;
  font-weight: 700;
  height: 47px;
  line-height: 21px;
  text-align: center;
`;

const DownloadButton = styled(BaseButton)`
  background: #444;
  border: 1px solid #333;
  color: #fff;
  width: 200px;
`;

const CloseActionButton = styled(BaseButton)`
  background: #ff5252;
  border: 1px solid #ff5252;
  color: #fff;
  width: 127px;
`;

const CompressButton = styled(BaseButton)`
  background: #ff5252;
  border: 1px solid #ff5252;
  color: #fff;
  width: 190px;
`;

interface PropTypes {
  sendGridError: boolean;
  setSendGridError: (sendGridError: boolean) => void;
  errorMessage?: string;
  possibleAttachmentSizeFailure?: boolean;
  canTryCompression?: boolean;
  multipleAttachments?: boolean;
  onClose?: () => void;
  onDownload?: () => void;
  onCompress?: () => void;
}

const SendGridErrorModal = (props: PropTypes) => {
  const isAttachmentSizeFailure = Boolean(props.possibleAttachmentSizeFailure);
  const canTryCompression = props.canTryCompression !== false;
  const errorMessage =
    props.errorMessage?.length > 0
      ? props.errorMessage
      : isAttachmentSizeFailure
      ? defaultAttachmentSizeErrorMessage
      : defaultErrorMessage;
  const modalInfo = isAttachmentSizeFailure
    ? `${errorMessage}\n\n${
        canTryCompression
          ? attachmentSizeChoiceHint
          : getCompressionUnavailableHint(Boolean(props.multipleAttachments))
      }`
    : errorMessage;

  const closeModal = () => props.setSendGridError(false);
  const handleDownload = () => {
    closeModal();
    if (isAttachmentSizeFailure) {
      props.onDownload?.();
    }
    props.onClose?.();
  };
  const handleCompress = () => {
    closeModal();
    props.onCompress?.();
  };

  return (
    <Modal
      open={props.sendGridError}
      onClose={closeModal}
      closeOnDimmerClick={false}
      style={{
        height: isAttachmentSizeFailure
          ? canTryCompression
            ? attachmentSizeFailureModalHeight
            : compressionUnavailableModalHeight
          : defaultModalHeight,
        width: isAttachmentSizeFailure ? 520 : 393,
      }}
    >
      <ModalContent>
        <CloseButton type="button" onClick={closeModal} aria-label="Close">
          <Icon name="close" />
        </CloseButton>
        <ModalBody>
          <ModalHeader>{isAttachmentSizeFailure ? 'Attachment Too Large!' : 'Email Downloaded!'}</ModalHeader>
          <ModalInfo style={isAttachmentSizeFailure ? { width: 430 } : undefined}>{modalInfo}</ModalInfo>
          <ButtonContainer style={isAttachmentSizeFailure ? { justifyContent: 'center' } : undefined}>
            {isAttachmentSizeFailure ? (
              <DownloadButton type="button" onClick={handleDownload}>
                Download Email
              </DownloadButton>
            ) : (
              <CloseActionButton type="button" onClick={handleDownload}>
                Close
              </CloseActionButton>
            )}
            {isAttachmentSizeFailure && canTryCompression && Boolean(props.onCompress) && (
              <CompressButton type="button" onClick={handleCompress}>
                Compress PDFs
              </CompressButton>
            )}
          </ButtonContainer>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SendGridErrorModal;
